import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { SetupContext } from 'src/consumer/context/SetupContext'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import { getFeatureItemBySlug } from 'src/consumer/services/featureServices'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { isTokenInvalidOrAbsent } from 'src/views/pages/login/ProtectedRoute'
import { LoadingSpinner } from '../Loaders'
import PrewallAndPaywall from '../PrewallAndPaywall/PrewallAndPaywall'
import PublicAppHeader from '../PublicAppHeader'
import { ClubbedAuthModal } from '../SignIn/ClubbedAuthComponents'
import {
  InfoAndSingleActionComponentVarOne,
  InfoWithHeadingDescriptionAndImage,
  RelatedAppFeaturesComponent,
} from '../ListingPageComponentsAndHelpers/ListingPageComponents'
import FaqSection from '../PrewallAndPaywall/FaqSection'
import { FAQ_CATEGORIES, defaultBgImgId } from 'src/consumer/constants'
import { Dialog } from '@mui/material'
import { isUserAllowedToAccessContent } from '../ActivityPlansPage/activityPlanHelpers'
import { NewJournalPageForPublicPage } from '../Journal/Pages/NewJournal'
import { PublicAppFooter } from '../PublicAppFooter'
import { FAQ_LAYOUTS } from '../PrewallAndPaywall/faqComponents/utils'

export default function JournalLandingPage() {
  const navigate = useNavigate()
  const { slug, journalId } = useParams()

  const { profileData } = useContext(ProfileContext)
  const { setupData } = useContext(SetupContext)

  const [journalData, setJournalData] = useState()
  const [dataLoading, setDataLoading] = useState(false)

  const [showClubbedAuthModal, setShowClubbedAuthModal] = useState(false)
  const [prewallOpen, setPrewallOpen] = useState(false)
  const [typeOfClubbedAuthModal, setTypeOfClubbedAuthModal] = useState('login')

  const [journalDialogOpen, setJournalDialogOpen] = useState(false)

  async function pageSetup() {
    setDataLoading(true)

    let response
    if (slug) {
      response = await getFeatureItemBySlug('journal', slug, true)
    } else {
      response = await getFeatureItemBySlug('journal', journalId, false)
    }
    if (response?.status === 200 && response?.data && Object.keys(response?.data)?.length !== 0) {
      setJournalData(response?.data?.data?.journal)
      setDataLoading(false)
    } else {
      navigate('/404')
    }
  }

  useEffect(() => {
    pageSetup()
  }, [])

  const handleStartLandingPage = () => {
    if (isTokenInvalidOrAbsent(localStorage.getItem('token'))) {
      setShowClubbedAuthModal(true)
    } else {
      if (allowAccessToContent) {
        setJournalDialogOpen(true)
      } else {
        setPrewallOpen(true)
      }
    }
  }

  let allowAccessToContent
  if (journalData?.subscription?.id && journalData?.subscription?.id !== 'subscription_free') {
    if (localStorage.getItem('token')) {
      if (
        isUserAllowedToAccessContent(profileData?.subscriptionTypeId, journalData?.subscription?.id)
      ) {
        allowAccessToContent = true
      } else {
        allowAccessToContent = false
      }
    } else {
      allowAccessToContent = false
    }
  } else {
    allowAccessToContent = true
  }

  return (
    <>
      {journalData && (
        <SEOComponent
          title={`${journalData?.title} | AUMHUM`}
          description={
            journalData?.description?.replaceAll('"', '') ||
            `Take AUMHUM's free, clinically-vetted ${journalData?.title} test to see if you have the signs and symptoms.`
          }
          image={journalData && getImageLink(journalData?.coverIcon)}
          keywordsArr={['journal', 'journaling', 'self-discovery']}
        />
      )}
      {dataLoading ? (
        <LoadingSpinner bgColor="#F5F5F599" dark />
      ) : (
        <>
          <PublicAppHeader
            loginFunction={() => {
              setTypeOfClubbedAuthModal('login')
              setShowClubbedAuthModal(true)
            }}
            registerFunction={() => {
              setTypeOfClubbedAuthModal('signup')
              setShowClubbedAuthModal(true)
            }}
            config={{ showLinks: false }}
            propUserProfileData={profileData}
          />
          <ClubbedAuthModal
            open={showClubbedAuthModal}
            onClose={() => {
              setShowClubbedAuthModal(false)
            }}
            loginVariant={typeOfClubbedAuthModal === 'login'}
          />
          <InfoAndSingleActionComponentVarOne
            headline={journalData?.title}
            description={journalData?.description}
            image={getImageLink(journalData?.coverIcon, defaultBgImgId)}
            actionButtonText="Journal"
            clickFunction={() => handleStartLandingPage()}
          />
          <InfoWithHeadingDescriptionAndImage
            headline="Discover the Power of Journaling"
            description="Unlock a world of self-discovery and well-being through the art of journaling.
            Take a moment each day to reflect, express your thoughts, and track your journey.
            Our private journaling feature provides a safe space for your personal reflections.
            Try it now and embark on a transformative experience!"
            image={getImageLink('MEDIA-60bd7ffc6814475da7af7d40aa54e5a3')}
          />
          <RelatedAppFeaturesComponent domainsArray={[]} featuresArrayInOrder={['mentor']} />
          <FaqSection
            categoryIds={[FAQ_CATEGORIES.therapist_booking_page_faqs]}
            layoutType={FAQ_LAYOUTS.TWO_COLUMN_SIDEBAR}
          />
          <PrewallAndPaywall
            id="journal_play"
            secureData={setupData}
            wallIsOpen={prewallOpen}
            toggleWall={() => {
              setPrewallOpen((prev) => !prev)
            }}
          />
          <WriteJournalModal
            open={journalDialogOpen}
            onCloseFunction={() => setJournalDialogOpen(false)}
            blankJournalData={journalData}
          />
          <PublicAppFooter />
        </>
      )}
    </>
  )
}

///////////////

export const WriteJournalModal = ({ open, onCloseFunction, blankJournalData }) => {
  const [parentModalState, setParentModalState] = useState()

  function setupPage() {
    return null
  }

  useEffect(() => {
    setupPage()
  }, [])

  return (
    <Dialog
      open={open}
      onClose={onCloseFunction}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '90vw',
            height: '90vh',
            maxWidth: '1280px',
            maxHeight: '968px',
          },
        },
      }}
    >
      <NewJournalPageForPublicPage
        setParentModalState={setParentModalState}
        newEntryData={blankJournalData}
        toggleModal={() => {
          onCloseFunction()
        }}
        afterSubmitFunction={() => onCloseFunction()}
      />
    </Dialog>
  )
}
